import { useState } from "react";
import { MessageCircle, Users, Home as HomeIcon, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Reveal } from "@/components/Reveal";
import { Section, Eyebrow, PageHero } from "@/components/common";
import { QRCode } from "@/components/QRCode";
import { api } from "@/lib/api";
import { useContent } from "@/lib/useContent";
import { trackEvent } from "@/lib/analytics";

const INTERESTS = ["Phone Banking", "Door Canvassing", "Host a Meet & Greet", "Yard Sign Request", "Social Media", "Event Help"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-semibold text-slate-700 mb-1.5 block">{label}</span>
    {children}
  </label>
);

export default function GetInvolved() {
  const { data: content } = useContent();
  const [vol, setVol] = useState({ name: "", email: "", phone: "", zip_code: "", interests: [], sms_consent: false, message: "" });
  const [yard, setYard] = useState({ name: "", email: "", phone: "", address: "" });
  const [meet, setMeet] = useState({ name: "", email: "", phone: "", preferred_date: "", details: "" });
  const [busy, setBusy] = useState(false);

  const toggleInterest = (i) =>
    setVol((v) => ({ ...v, interests: v.interests.includes(i) ? v.interests.filter((x) => x !== i) : [...v.interests, i] }));

  const submit = async (path, body, reset) => {
    setBusy(true);
    try {
      await api.post(path, body);
      trackEvent("form_submit", { form: path.replace("/", "") });
      toast.success("Thank you! We've received your submission and will be in touch soon.");
      reset();
    } catch {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally { setBusy(false); }
  };

  if (!content) return <div className="h-screen" />;
  const whatsapp = content.whatsapp_url;

  return (
    <div>
      <PageHero eyebrow="Get Involved" title="Join Team Satish" subtitle="This is a people-powered campaign. Whatever time you have, there's a way to help bring People First, Community Always to Annapolis." />

      <Section className="py-16">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Tabs defaultValue="volunteer" className="w-full" data-testid="involve-tabs">
              <TabsList className="grid grid-cols-3 w-full mb-6">
                <TabsTrigger value="volunteer" data-testid="tab-volunteer"><Users className="w-4 h-4 mr-1.5" />Volunteer</TabsTrigger>
                <TabsTrigger value="yard" data-testid="tab-yard"><HomeIcon className="w-4 h-4 mr-1.5" />Yard Sign</TabsTrigger>
                <TabsTrigger value="meet" data-testid="tab-meet"><Calendar className="w-4 h-4 mr-1.5" />Meet &amp; Greet</TabsTrigger>
              </TabsList>

              {/* VOLUNTEER */}
              <TabsContent value="volunteer">
                <form onSubmit={(e) => { e.preventDefault(); submit("/volunteer", vol, () => setVol({ name: "", email: "", phone: "", zip_code: "", interests: [], sms_consent: false, message: "" })); }}
                  className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-5" data-testid="volunteer-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full Name *"><Input required value={vol.name} onChange={(e) => setVol({ ...vol, name: e.target.value })} data-testid="vol-name" /></Field>
                    <Field label="Email *"><Input type="email" required value={vol.email} onChange={(e) => setVol({ ...vol, email: e.target.value })} data-testid="vol-email" /></Field>
                    <Field label="Phone"><Input type="tel" value={vol.phone} onChange={(e) => setVol({ ...vol, phone: e.target.value })} data-testid="vol-phone" /></Field>
                    <Field label="ZIP Code"><Input value={vol.zip_code} onChange={(e) => setVol({ ...vol, zip_code: e.target.value })} data-testid="vol-zip" /></Field>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-slate-700 mb-2 block">How would you like to help?</span>
                    <div className="grid sm:grid-cols-2 gap-2.5">
                      {INTERESTS.map((i) => (
                        <label key={i} className="flex items-center gap-2.5 bg-sky/50 border border-sky-border rounded-lg px-3 py-2.5 cursor-pointer hover:bg-sky transition-colors">
                          <Checkbox checked={vol.interests.includes(i)} onCheckedChange={() => toggleInterest(i)} data-testid={`vol-interest-${i.slice(0, 5).toLowerCase()}`} />
                          <span className="text-sm text-slate-700">{i}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Field label="Anything else?"><Textarea value={vol.message} onChange={(e) => setVol({ ...vol, message: e.target.value })} data-testid="vol-message" /></Field>
                  <label className="flex items-start gap-2.5 text-xs text-slate-500 leading-relaxed cursor-pointer">
                    <Checkbox checked={vol.sms_consent} onCheckedChange={(v) => setVol({ ...vol, sms_consent: !!v })} className="mt-0.5" data-testid="vol-sms-consent" />
                    <span>I consent to receive recurring campaign text messages from Friends of Satish Chapagain. Msg &amp; data rates may apply. Reply STOP to opt out.</span>
                  </label>
                  <Button type="submit" disabled={busy} className="w-full bg-brightblue hover:bg-brightblue-hover text-white font-semibold h-11" data-testid="vol-submit">Sign Up to Volunteer</Button>
                </form>
              </TabsContent>

              {/* YARD SIGN */}
              <TabsContent value="yard">
                <form onSubmit={(e) => { e.preventDefault(); submit("/yard-sign", yard, () => setYard({ name: "", email: "", phone: "", address: "" })); }}
                  className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-5" data-testid="yard-form">
                  <p className="text-slate-600 text-sm">Show your support! Request a free yard sign and we'll arrange delivery to your home in District 7A.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full Name *"><Input required value={yard.name} onChange={(e) => setYard({ ...yard, name: e.target.value })} data-testid="yard-name" /></Field>
                    <Field label="Email *"><Input type="email" required value={yard.email} onChange={(e) => setYard({ ...yard, email: e.target.value })} data-testid="yard-email" /></Field>
                    <Field label="Phone"><Input type="tel" value={yard.phone} onChange={(e) => setYard({ ...yard, phone: e.target.value })} data-testid="yard-phone" /></Field>
                  </div>
                  <Field label="Delivery Address *"><Textarea required value={yard.address} onChange={(e) => setYard({ ...yard, address: e.target.value })} placeholder="Street, City, ZIP" data-testid="yard-address" /></Field>
                  <Button type="submit" disabled={busy} className="w-full bg-brightblue hover:bg-brightblue-hover text-white font-semibold h-11" data-testid="yard-submit">Request a Yard Sign</Button>
                </form>
              </TabsContent>

              {/* MEET & GREET */}
              <TabsContent value="meet">
                <form onSubmit={(e) => { e.preventDefault(); submit("/meet-greet", meet, () => setMeet({ name: "", email: "", phone: "", preferred_date: "", details: "" })); }}
                  className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-5" data-testid="meet-form">
                  <p className="text-slate-600 text-sm">Host Satish in your home or neighborhood! Gather friends and neighbors for a conversation about District 7A.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full Name *"><Input required value={meet.name} onChange={(e) => setMeet({ ...meet, name: e.target.value })} data-testid="meet-name" /></Field>
                    <Field label="Email *"><Input type="email" required value={meet.email} onChange={(e) => setMeet({ ...meet, email: e.target.value })} data-testid="meet-email" /></Field>
                    <Field label="Phone"><Input type="tel" value={meet.phone} onChange={(e) => setMeet({ ...meet, phone: e.target.value })} data-testid="meet-phone" /></Field>
                    <Field label="Preferred Date"><Input type="date" value={meet.preferred_date} onChange={(e) => setMeet({ ...meet, preferred_date: e.target.value })} data-testid="meet-date" /></Field>
                  </div>
                  <Field label="Tell us about your event"><Textarea value={meet.details} onChange={(e) => setMeet({ ...meet, details: e.target.value })} placeholder="Location, expected guests, ideas..." data-testid="meet-details" /></Field>
                  <Button type="submit" disabled={busy} className="w-full bg-brightblue hover:bg-brightblue-hover text-white font-semibold h-11" data-testid="meet-submit">Request to Host</Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>

          {/* SIDEBAR */}
          <div className="space-y-6">
            <Reveal className="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-2xl p-6 shadow-sm">
              <MessageCircle className="w-9 h-9" />
              <h3 className="font-serif text-xl font-bold mt-3">Join our WhatsApp community</h3>
              <p className="text-green-50 text-sm mt-2">Get real-time updates and connect with other supporters.</p>
              <Button asChild className="mt-4 w-full bg-white text-green-700 hover:bg-green-50 font-semibold" data-testid="whatsapp-join-btn">
                <a href={whatsapp} target="_blank" rel="noopener noreferrer">Join the Group</a>
              </Button>
            </Reveal>

            <Reveal delay={0.1} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm text-center">
              <Eyebrow>Share &amp; Recruit</Eyebrow>
              <p className="text-sm text-slate-600 mb-4">Scan or download to invite neighbors to volunteer.</p>
              <div className="flex justify-center">
                <QRCode data={`${window.location.origin}/get-involved`} label="Volunteer" filename="satish-volunteer" size={150} />
              </div>
            </Reveal>
          </div>
        </div>
      </Section>
    </div>
  );
}
